/**
 * THE ONE THING A PRODUCT CARD KNOWS THAT IS ABOUT OTHER SHOPPERS.
 *
 * The owner: "let's show labeling of hot on products (based on what most opened
 * / top 10 opened or viewed)". Nothing here could answer that — `salon_page_view`
 * is salon-level and carries no product, `product_collection_event` only sees a
 * product reached THROUGH a collection, and the `product_view` events this app
 * posts go to external analytics rather than to us. So the server grew a
 * measurement (`product_open`, one row per visitor per product per day) and this
 * file is its whole client.
 *
 * ── ONE REQUEST FOR A WHOLE SHOP, NOT ONE PER CARD ─────────────────────────
 *
 * The same rule `productCardSignals.js` had to learn for the liked set, for the
 * same reason: a grid is n cards, and n round trips to answer one question is
 * how the 3.4 MB first paint happened. The hot list is at most twenty-four
 * uuids, so it is fetched ONCE per shop slug and every card reads the memoised
 * answer — the grid, the collection page, the brand page and the strips alike.
 *
 * Keyed by SLUG, not global: the storefront can move between shops without a
 * reload (the customer home, a shared link), and one shop's popularity leaking
 * into another's grid is the exact multi-tenancy failure the server refuses at
 * its own end. Both ends refuse it.
 *
 * ── PARTIAL MOCKS ─────────────────────────────────────────────────────────
 *
 * Several suites mock `api/storeApi` with an explicit factory naming only the
 * functions their surface needs. A card calling a function such a mock omits
 * would THROW on mount rather than fail an assertion, so the call is guarded on
 * the export actually being callable — the same "second render site, silent
 * no-op" shape this codebase has shipped once.
 */
import { useEffect, useState } from 'react';
import * as storeApi from '../../api/storeApi';

const callable = (name) => (typeof storeApi?.[name] === 'function' ? storeApi[name] : null);

/** `data-testid` prefix; the product id is appended. Read by the guards. */
export const HOT_BADGE_TEST_ID = 'shop-product-hot';

/** slug → Promise<Set<string>>. One in flight per shop, ever. */
const bySlug = new Map();

/** Test seam and shop-change reset. */
export function resetHotProducts() {
  bySlug.clear();
}

/**
 * The hot ids for one shop. Resolves to an EMPTY SET on anything unexpected —
 * a shop with no traffic, an owner who switched the label off, a 404, a network
 * failure. A badge that appears because a request failed is worse than no badge.
 */
export function fetchHotProductIds(salonSlug) {
  if (!salonSlug) return Promise.resolve(new Set());
  if (bySlug.has(salonSlug)) return bySlug.get(salonSlug);
  const fn = callable('fetchHotProducts');
  if (!fn) {
    bySlug.set(salonSlug, Promise.resolve(new Set()));
    return bySlug.get(salonSlug);
  }
  const p = Promise.resolve()
    .then(() => fn(salonSlug))
    .then((res) => (res && res.enabled && Array.isArray(res.productIds)
      ? new Set(res.productIds)
      : new Set()))
    .catch(() => new Set());
  bySlug.set(salonSlug, p);
  return p;
}

/**
 * `false` until the answer is known, then whether THIS product is hot.
 *
 * Starting at `false` rather than at `null` is deliberate: the badge must never
 * flash on and off, and "not yet known" and "not hot" look identical to a
 * shopper. Only the transition to true ever draws anything.
 */
export function useIsHotProduct(salonSlug, productId) {
  const [hot, setHot] = useState(false);
  useEffect(() => {
    if (!salonSlug || !productId) return undefined;
    let cancelled = false;
    fetchHotProductIds(salonSlug).then((ids) => {
      if (!cancelled) setHot(ids.has(productId));
    });
    return () => { cancelled = true; };
  }, [salonSlug, productId]);
  return hot;
}

// ── Recording an open ──────────────────────────────────────────────────────

export const OPEN_KEY_PREFIX = 'stylon:product-open:';

/**
 * ONCE PER PRODUCT PER SESSION, from the client — and once per visitor per DAY
 * at the server, which is the rule that actually counts. Two guards, because
 * they refuse different things: this one stops a shopper flicking back and
 * forth between a product and the grid from putting ten requests on the wire,
 * and the server's unique key stops a new tab, a cleared session or a second
 * device from becoming a second shopper on the same day.
 */
export function recordProductOpen(salonSlug, productId) {
  if (!salonSlug || !productId) return;
  const key = `${OPEN_KEY_PREFIX}${salonSlug}:${productId}`;
  try {
    if (sessionStorage.getItem(key) === '1') return;
    sessionStorage.setItem(key, '1');
  } catch (_) {
    // sessionStorage unavailable (private mode). The server's per-day key is
    // the real guard; falling through here costs at most a duplicate no-op.
  }
  const fn = callable('recordProductOpen');
  if (!fn) return;
  // Fire-and-forget from a macrotask, exactly like `recordCollectionEvent`:
  // instrumentation must never block a render or surface an error to a shopper.
  setTimeout(() => {
    try {
      Promise.resolve(fn(salonSlug, productId)).catch(() => {});
    } catch (_) { /* never throw from instrumentation */ }
  }, 0);
}
